import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { ApiError } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import type { KratosFlow } from "@/lib/kratos";
import { firstFlowError, nodeMessages } from "@/lib/kratos";
import { exchangeKratosSession } from "@/features/auth/kratos/exchange";
import { KratosPasswordForm } from "@/features/auth/kratos/KratosPasswordForm";
import { KratosStepUpPanel } from "@/features/auth/kratos/KratosStepUpPanel";
import { useKratosFlow } from "@/features/auth/kratos/useKratosFlow";

/**
 * Account recovery, all in-app: email → one-time code → new password.
 *
 * A valid code makes Kratos issue a privileged session and hand back a
 * settings flow (`browser_location_change_required`, followed by flow
 * id in the kratos client) — `onFlowChange` swaps the UI to the
 * password form, and a saved password exchanges into a Tktic session.
 * The email step is enumeration-safe: unknown addresses get the same
 * "code sent" state.
 */
export function KratosRecoveryPanel({ flowId }: { flowId?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [settingsFlow, setSettingsFlow] = useState<KratosFlow | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState(false);

  const { flow, loading, submitting, error, submit } = useKratosFlow("recovery", {
    flowId,
    onFlowChange: (nextFlow, nextKind) => {
      if (nextKind === "settings") setSettingsFlow(nextFlow);
    },
  });

  const finishRecovery = async () => {
    try {
      await exchangeKratosSession();
    } catch (err) {
      // Recovery does not bypass the per-user MFA-at-login gate (an email
      // inbox alone must not defeat MFA). Collect the second factor inline,
      // exactly like the login panel. Other errors keep propagating to the
      // password form's own error handling.
      if (
        err instanceof ApiError &&
        err.detail &&
        typeof err.detail === "object" &&
        "code" in err.detail &&
        String((err.detail as { code: unknown }).code) === "mfa_login_required"
      ) {
        setMfaChallenge(true);
        return;
      }
      throw err;
    }
    await queryClient.invalidateQueries();
    await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
    void navigate({ to: "/hub", replace: true });
  };

  if (mfaChallenge) {
    // The step-up panel exchanges + invalidates itself, then navigates.
    return (
      <div className="flex flex-col gap-3" data-testid="recovery-mfa-challenge">
        <KratosStepUpPanel variant="login" nextPath="/hub" allowRememberDevice />
      </div>
    );
  }

  if (settingsFlow) {
    return (
      <section className="flex flex-col gap-3" data-testid="recovery-password-step">
        <p className="text-sm text-ink-muted">
          Code accepted. Choose a new password to finish recovering your
          account.
        </p>
        <KratosPasswordForm
          initialFlow={settingsFlow}
          onSaved={finishRecovery}
          testIdPrefix="recovery-password"
        />
      </section>
    );
  }

  if (loading || !flow) {
    return (
      <p data-testid="recovery-loading" className="font-mono text-xs text-ink-muted">
        Preparing account recovery...
      </p>
    );
  }

  const flowError = firstFlowError(flow);
  const codeMessages = nodeMessages(flow, "code");

  if (flow.state === "sent_email") {
    return (
      <form
        data-testid="recovery-code-form"
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!code.trim() || submitting) return;
          void submit({ method: "code", code: code.trim() });
        }}
      >
        <p className="text-sm text-ink-muted">
          If an account exists for that email, a recovery code is on the
          way. Enter it below.
        </p>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Recovery code
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            required
            data-testid="recovery-code"
            className="rounded border border-rule bg-canvas px-3 py-2.5 font-mono text-sm tracking-[0.3em] text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
          {codeMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>
        {flowError ? (
          <p data-testid="recovery-error" className="font-mono text-xs text-destructive">
            {flowError}
          </p>
        ) : null}
        {error ? <p className="font-mono text-xs text-destructive">{error}</p> : null}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              if (submitting) return;
              void submit({ method: "code", email: email.trim() || undefined });
            }}
            data-testid="recovery-resend"
            className="rounded px-1 py-0.5 font-mono text-[0.65rem] text-ink-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Resend code
          </button>
          <Button type="submit" disabled={submitting} data-testid="recovery-submit">
            {submitting ? (
              "Checking..."
            ) : (
              <>
                Continue
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      data-testid="recovery-email-form"
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!email.trim() || submitting) return;
        void submit({ method: "code", email: email.trim() });
      }}
    >
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Email
        </span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoFocus
          required
          data-testid="recovery-email"
          className="rounded border border-rule bg-canvas px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      <p className="text-[0.6rem] font-mono text-ink-muted">
        We&rsquo;ll email a one-time recovery code to that address.
      </p>
      {flowError ? (
        <p data-testid="recovery-error" className="font-mono text-xs text-destructive">
          {flowError}
        </p>
      ) : null}
      {error ? <p className="font-mono text-xs text-destructive">{error}</p> : null}
      <Button type="submit" disabled={submitting} data-testid="recovery-send" className="self-end">
        {submitting ? "Sending..." : "Send recovery code"}
      </Button>
    </form>
  );
}
